import React, { useCallback, useContext, useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Spinner } from "@/components/ui/spinner";
import Container from "@/components/layouts/Container";
import { useAuth } from "@/context/providers/AuthProvider";
import { resendVerificationOtp, validateVerificationOTP } from "@/services/auth.service";
import { OTP_VALIDATE } from "@/utils/constants/appConstants/OTPconstants";
import { BUTTONS, HEADINGS, PLACEHOLDERS } from "@/utils/constants/appConstants/commonConstants";
import { OTP_RESENT, SOMETHING_WENT_WRONG } from "@/utils/constants/messages";
import { getCurrentOrganization } from "@/utils/functions/OrganizationFunctions";
import { getName, getUserId, getemail_address } from "@/utils/functions/TokenFunction";
import { getFromLocalStorage, getInitials, setDataInLocalStorage, storeLoginUserData } from "@/utils/functions/helperFunctions";
import { errorToasterMessage, successToasterMessage } from "@/utils/functions/toaster";
import { OrganizationContext } from "@/context/providers/OrganizationProvider";
import { cn } from "@/lib/utils";

const OTPScreen: React.FC = () => {
    const [searchParams] = useSearchParams();
    const id = getUserId();
    const session_id = getFromLocalStorage("session_id");
    const otp_session_token = getFromLocalStorage("otp_session_token");
    const organization =
        searchParams.get("organization") ||
        getFromLocalStorage("switch_org_id") ||
        getCurrentOrganization()?.organization;
    const [validateLoading, setValidateLoading] = useState(false);
    const [resetLoading, setResetLoading] = useState(false);
    const [name, setName] = useState("");
    const [email, setEmail] = useState("");
    const [otp, setOtp] = useState("");
    const navigate = useNavigate();
    const { setToken } = useAuth();
    const { setOrganization } = useContext(OrganizationContext);

    useEffect(() => {
        localStorage.removeItem("switch_org_id");
        setName(getName());
        setEmail(getemail_address());
    }, []);

    const handleOtpValidate = useCallback(
        (e: React.FormEvent<HTMLFormElement>) => {
            e.preventDefault();
            setValidateLoading(true);
            validateVerificationOTP(
                {
                    email_address: email,
                    code: otp,
                    user_id: id,
                    session_id,
                    otp_session_token,
                    portal_type: "admin",
                    organization,
                },
                (response: any) => {
                    if (!response?.data?.access_token) {
                        setValidateLoading(false);
                        errorToasterMessage(SOMETHING_WENT_WRONG);
                        return;
                    }

                    storeLoginUserData(response.data);
                    setOrganization(response.data.current_organization);
                    setToken(response.data.access_token);
                    localStorage.removeItem("loginWithSSO");
                    navigate("/");
                },
                (err: any) => {
                    setValidateLoading(false);
                    errorToasterMessage(err);
                }
            );
        },
        [email, otp, id, session_id, otp_session_token, organization, navigate, setOrganization, setToken]
    );

    const handleResentOTP = useCallback(() => {
        if (!otp_session_token) {
            errorToasterMessage(SOMETHING_WENT_WRONG);
            return;
        }
        const loginPassword = sessionStorage.getItem("admin_login_password");
        setResetLoading(true);
        resendVerificationOtp(
            {
                email_address: email,
                user_id: id,
                session_id,
                otp_session_token,
                portal_type: "admin",
                organization,
                ...(loginPassword ? { password: loginPassword } : {}),
            },
            (response: any) => {
                setResetLoading(false);
                if (response?.data?.otp_session_token) {
                    setDataInLocalStorage("otp_session_token", response.data.otp_session_token);
                }
                successToasterMessage(response?.data?.message || OTP_RESENT);
            },
            (er: any) => {
                setResetLoading(false);
                errorToasterMessage(er);
            }
        );
    }, [email, id, session_id, otp_session_token, organization]);

    return (
        <Container>
            <h2 className="gs-otp-screen__title" id="opt-two-factor-authenticate-heading">
                {HEADINGS.TWO_FACTOR_AUTHENTICATION}
            </h2>

            <div className="gs-otp-screen__user-bar">
                <Avatar className="size-10 shrink-0 bg-primary">
                    <AvatarFallback
                        className="bg-primary text-sm font-medium tracking-wider text-white"
                        id="opt-form-user-initials"
                    >
                        {getInitials(name || "")}
                    </AvatarFallback>
                </Avatar>
                <div className="min-w-0">
                    <span className="gs-otp-screen__user-name" id="opt-form-user-name">
                        {name}
                    </span>
                    <span className="gs-otp-screen__user-email" id="opt-form-user-email">
                        {email}
                    </span>
                </div>
            </div>

            <div className="gs-otp-screen__message">
                <span id="opt-two-factor-authenticate-message">{OTP_VALIDATE.TEXT(email as string)}</span>
                <br />
                <span>{OTP_VALIDATE.SUB_TEXT}</span>
            </div>

            <form onSubmit={handleOtpValidate} className="w-full">
                <Input
                    id="otp-form-input"
                    type="text"
                    value={otp}
                    onChange={(e) => setOtp(e.target.value)}
                    placeholder={PLACEHOLDERS.OTP_PLACEHOLDER}
                    required
                    autoFocus
                    className="gs-otp-screen__input w-full"
                />
                <div className="gs-otp-screen__actions">
                    <Button
                        type="submit"
                        id="otp-validate-btn"
                        disabled={validateLoading}
                        aria-busy={validateLoading || undefined}
                        className={cn("gs-otp-screen__btn relative", validateLoading && "gs-btn-loading")}
                    >
                        <span className={cn(validateLoading && "invisible")}>{BUTTONS.VALIDATE}</span>
                        {validateLoading ? (
                            <span className="gs-otp-screen__btn-loader" aria-hidden>
                                <Spinner
                                    size="sm"
                                    className="text-white"
                                    id="otp-validate-btn-loader"
                                />
                            </span>
                        ) : null}
                    </Button>
                    <Button
                        type="button"
                        variant="outline"
                        id="otp-resend-otp-btn"
                        disabled={resetLoading}
                        aria-busy={resetLoading || undefined}
                        onClick={handleResentOTP}
                        className={cn(
                            "gs-otp-screen__btn gs-otp-screen__btn-outline relative",
                            resetLoading && "gs-btn-loading"
                        )}
                    >
                        <span className={cn(resetLoading && "invisible")}>
                            {BUTTONS.RESEND_OTP}
                        </span>
                        {resetLoading ? (
                            <span className="gs-otp-screen__btn-loader" aria-hidden>
                                <Spinner
                                    size="sm"
                                    className="text-primary"
                                    id="otp-resend-otp-btn-loader"
                                />
                            </span>
                        ) : null}
                    </Button>
                </div>
            </form>
        </Container>
    );
};

export default OTPScreen;
